import Close from '@mui/icons-material/Close'
import { Dialog, IconButton } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { generateHexWithOpacity, isLightColor } from 'src/consumer/helpers/utilFunctions'
import BookedEventInfoActionDialog from 'src/supportPortal/components/common/bookedEventInfo/BookedEventInfoActionDialog'
import ReschedulePopup from 'src/supportPortal/components/common/bookedEventInfo/components/ReschedulePopup'
import { useRosterData } from 'src/customHooks/useRosterData'
import { usePopoverState } from 'src/customHooks/usePopoverState'
import { useCalendarState } from 'src/customHooks/useCalendarState'
import { useRosterMode } from 'src/customHooks/useRosterMode'
import RosterCalendar from './RosterCalendar'
import RosterSidebar from './RosterSidebar'
import RosterPopover from './RosterPopover'
import { BookingInfoDialogPaper, RosterPageContainer } from './styledComponents'
import { buildNewRosterRequest, getFullCalendarConvertedDateFromDayJsDate } from './utils'
import { filterBookedSlots, DEFAULT_FILTERS } from './utils/filterUtils'

export default function RosterComponent({
  // Display configuration
  showSidebar = true,
  showDatePicker = true,
  showWeekPicker = true,
  showFilters = true,
  showLegend = true,
  showItemSelector = true,
  showRosterItems = true,
  showBookedSlots = true,
  enableReschedule = true,
  enableSlotCreation = true,
  enableSlotSelection = true,
  hideCalendarHeader = false,
  hideAllDaySlot = false,

  // Date restrictions
  minDate,
  maxDate,
  lockedDates = [],

  // Calendar configuration
  calendarView = 'timeGridDay', // 'dayGridMonth', 'timeGridWeek', 'timeGridDay'
  slotDuration = '00:30:00',
  slotMinTime = '00:00:00',
  slotMaxTime = '24:00:00',

  // Event callbacks
  onSlotSelect,
  onSlotClick,
  onBookedEventClick,
  onDateChange,

  // Layout configuration
  containerClassName = '',
  sidebarClassName = '',
  calendarClassName = '',
  disableGrid = false,
  rosterPageContainerStyles = {},

  // Data related configuration
  bookedSlotsTransformer = (bookedSlots) => bookedSlots,

  // Original props
  loadingData,
  hideOptionSelectionDropDown = false,
  itemOptions,
  getOptionValue,
  getOptionLabel,
  sidebarLegends,
  getSidebarLegendItems,
  getSidebarLegendLabel,
  getSidebarLegendValue,
  getSidebarItemReferenceFromRosterItem,
  rosterListFetchApiSignature,
  bookedSlotsFetchApiSignature,
  legendTitle,
  selectedItem,
  setSelectedItem,
  rosterApiIdParamKey,
  RosterFormComponent,
  RosterSlotViewComponent,
  bookedSlotsApiIdParamKey,
  canUserSelectRosterItemMethod,
  preSelectedDate,
  getSelectedItemSchedules = () => [],
  getSidebarLegendValueBySecondaryKey,
  rosterRequestBuilder = ({
    startTimeString,
    endTimeString,
    currentDateString,
    startDateTime,
    endDateTime,
    selectedItem,
  }) => {
    return buildNewRosterRequest(
      selectedItem?.clinicBrandId,
      selectedItem?.clinicLocationId,
      selectedItem?.id,
      null,
      currentDateString,
      startTimeString,
      endTimeString,
    )
  },
  blockedText = 'Blocked',
  slotBlockedText = 'Slot Blocked',
}) {
  // Use custom hooks
  const calendarState = useCalendarState(preSelectedDate, minDate, maxDate, lockedDates)
  const popoverState = usePopoverState()
  const modeState = useRosterMode()

  const { rosterList, bookedSlots, loading, setRosterList, refreshBookedSlots } = useRosterData({
    selectedItem,
    currentDate: calendarState.currentDate,
    toDate: calendarState.toDate,
    getOptionValue,
    rosterListFetchApiSignature,
    bookedSlotsFetchApiSignature,
    rosterApiIdParamKey,
    bookedSlotsApiIdParamKey,
    bookedSlotsTransformer,
  })

  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  const legendItems = useMemo(() => {
    let legItems = []
    if (!!getSidebarLegendItems) {
      legItems = getSidebarLegendItems(itemOptions)
    }
    if (!!sidebarLegends) {
      legItems = sidebarLegends
    }
    legItems = legItems?.map((lItem) => {
      const generatedColor = generateHexWithOpacity()
      const color = isLightColor(generatedColor) ? '#000' : '#FFF'

      return {
        ...lItem,
        bgColor: generatedColor,
        textColor: color,
        borderColor: '#cbcbcb',
      }
    })
    return legItems
  }, [sidebarLegends, getSidebarLegendItems, itemOptions])

  const createSlot = useCallback(
    (startDateTime, endDateTime, e) => {
      if (!enableSlotCreation || loadingData) {
        calendarState.unselectSlot()
        return
      }

      // Check date restrictions
      const slotDate = dayjs(startDateTime)
      if (!calendarState.isDateSelectable(slotDate, minDate, maxDate, lockedDates)) {
        calendarState.unselectSlot()
        return
      }

      // Custom callback
      if (onSlotSelect) {
        onSlotSelect({ startDateTime, endDateTime, event: e })
        return
      }

      if (modeState.currentMode === 'reassign' && enableReschedule) {
        modeState.openReschedulePopup(startDateTime)
        return
      }

      const rosterRequest = rosterRequestBuilder({
        endTimeString: dayjs(endDateTime).format('HH:mm'),
        currentDateString: getFullCalendarConvertedDateFromDayJsDate(dayjs(startDateTime)),
        startTimeString: dayjs(startDateTime).format('HH:mm'),
        selectedItem,
      })

      popoverState.openCreatePopover(rosterRequest, e.target)
    },
    [
      enableSlotCreation,
      loadingData,
      onSlotSelect,
      enableReschedule,
      rosterRequestBuilder,
      selectedItem,
      calendarState,
      minDate,
      maxDate,
      lockedDates,
      modeState,
      popoverState,
    ],
  )

  const handleRosterItemSelected = useCallback(
    (rosterItem, e) => {
      if (!enableSlotSelection) return

      if (onSlotClick) {
        onSlotClick({ rosterItem, event: e })
        return
      }

      if (modeState.currentMode === 'reassign') {
        return
      }
      let canSelectItem = true
      if (canUserSelectRosterItemMethod) {
        canSelectItem = canUserSelectRosterItemMethod(rosterItem)
      }
      if (canSelectItem) {
        popoverState.openViewPopover(rosterItem, e.target)
      }
    },
    [
      enableSlotSelection,
      onSlotClick,
      modeState.currentMode,
      canUserSelectRosterItemMethod,
      popoverState,
    ],
  )

  const handleClickBookedEvent = useCallback(
    ({ bookedEventInfo }) => {
      if (onBookedEventClick) {
        onBookedEventClick({ bookedEventInfo })
        return
      }

      if (modeState.currentMode !== 'default') {
        return
      }
      modeState.openBookedEventDialog(bookedEventInfo)
    },
    [onBookedEventClick, modeState],
  )

  const handleDateChange = useCallback(
    (date) => {
      calendarState.handleChangeDate(date)
      if (onDateChange) {
        onDateChange(date)
      }
    },
    [calendarState, onDateChange],
  )

  const addRosterItemToList = (rosterItem) => {
    setRosterList((prev) => [
      ...prev,
      { ...rosterItem, bgColor: generateHexWithOpacity(), borderColor: '#cbcbcb' },
    ])
  }

  const removeSlotFromRosterList = (rosterId) => {
    setRosterList((prev) => (prev || [])?.filter((rost) => rost?.id !== rosterId))
  }

  const afterRescheduling = async (updatedSlot) => {
    modeState.closeReschedulePopup()
    calendarState.unselectSlot()
    modeState.resetToDefaultMode()
    modeState.setSelectedBookedEvent((prev) => ({ ...updatedSlot, patientInfo: prev?.patientInfo }))
    await refreshBookedSlots()
  }

  useEffect(() => {
    if (popoverState.createPopoverAnchor === null) {
      calendarState.unselectSlot()
    }
  }, [popoverState.createPopoverAnchor, calendarState])

  const filteredBookedSlots = filterBookedSlots(bookedSlots, filters, modeState.currentMode)

  const containerClass = `roster-component-container ${containerClassName}`

  return (
    <RosterPageContainer
      disableGrid={disableGrid}
      style={rosterPageContainerStyles}
      className={containerClass}
    >
      {showSidebar && (
        <div className={sidebarClassName}>
          <RosterSidebar
            showDatePicker={showDatePicker}
            showWeekPicker={showWeekPicker}
            showFilters={showFilters}
            showLegend={showLegend}
            showItemSelector={showItemSelector}
            minDate={minDate}
            maxDate={maxDate}
            lockedDates={lockedDates}
            hideOptionSelectionDropDown={hideOptionSelectionDropDown}
            loadingData={loadingData}
            itemOptions={itemOptions}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            currentView={calendarState.currentView}
            currentDate={calendarState.currentDate}
            handleChangeDate={handleDateChange}
            filters={filters}
            setFilters={setFilters}
            legendItems={legendItems}
            getSidebarLegendLabel={getSidebarLegendLabel}
            legendTitle={legendTitle}
          />
        </div>
      )}

      <div className={calendarClassName}>
        <RosterCalendar
          calendarRef={calendarState.calendarRef}
          rosterList={showRosterItems && filters?.rosterItems ? rosterList : []}
          bookedSlots={showBookedSlots ? filteredBookedSlots : []}
          legendItems={legendItems}
          handleEventClick={handleRosterItemSelected}
          handleClickBookedEvent={handleClickBookedEvent}
          getSidebarItemReferenceFromRosterItem={getSidebarItemReferenceFromRosterItem}
          getSidebarLegendValue={getSidebarLegendValue}
          getSidebarLegendLabel={getSidebarLegendLabel}
          subHeading={
            modeState.currentMode === 'reassign' && enableReschedule ? (
              <div className="d-flex align-items-center justify-content-between gap-1">
                <span>Rescheduling for {modeState.selectedBookedEvent?.mentorName}</span>
                <IconButton onClick={modeState.resetToDefaultMode}>
                  <Close />
                </IconButton>
              </div>
            ) : (
              ''
            )
          }
          getSidebarLegendValueBySecondaryKey={getSidebarLegendValueBySecondaryKey}
          handleDatesSet={calendarState.handleFullCalendarDateChange}
          rosterItemSchedules={getSelectedItemSchedules(selectedItem)}
          onSlotSelect={createSlot}
          blockedText={blockedText}
          // Pass calendar configuration
          initialView={calendarView}
          slotDuration={slotDuration}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          hideCalendarHeader={hideCalendarHeader}
          slotBlockedText={slotBlockedText}
          hideAllDaySlot={hideAllDaySlot}
        />
      </div>

      {/* Only show popovers if components are provided */}
      {RosterFormComponent && (
        <RosterPopover
          open={popoverState.createPopoverAnchor}
          anchorEl={popoverState.createPopoverAnchor}
          onClose={popoverState.closeAllPopovers}
        >
          <RosterFormComponent
            selectedSlot={popoverState.selectedSlot}
            closePopup={popoverState.closeAllPopovers}
            legendItems={legendItems}
            getSidebarLegendValue={getSidebarLegendValue}
            getSidebarLegendLabel={getSidebarLegendLabel}
            getSidebarItemReferenceFromRosterItem={getSidebarItemReferenceFromRosterItem}
            selectedItem={selectedItem}
            addEventToSlotList={addRosterItemToList}
            updateSlotOnCalendar={calendarState.updateSelectedSlot}
          />
        </RosterPopover>
      )}

      {RosterSlotViewComponent && (
        <RosterPopover
          open={popoverState.viewPopoverAnchor}
          anchorEl={popoverState.viewPopoverAnchor}
          onClose={popoverState.closeAllPopovers}
        >
          <RosterSlotViewComponent
            selectedSlot={popoverState.selectedSlot}
            closePopup={popoverState.closeAllPopovers}
            legendItems={legendItems}
            getSidebarLegendValue={getSidebarLegendValue}
            getSidebarLegendLabel={getSidebarLegendLabel}
            getSidebarItemReferenceFromRosterItem={getSidebarItemReferenceFromRosterItem}
            selectedItem={selectedItem}
            addEventToSlotList={addRosterItemToList}
            updateSlotOnCalendar={calendarState.updateSelectedSlot}
            removeSlotFromRosterList={removeSlotFromRosterList}
          />
        </RosterPopover>
      )}

      {enableReschedule && (
        <>
          <Dialog
            open={modeState.showBookedEventDialog}
            onClose={modeState.closeBookedEventDialog}
            PaperComponent={BookingInfoDialogPaper}
          >
            <BookedEventInfoActionDialog
              bookedEventData={modeState.selectedBookedEvent}
              closeDialog={modeState.closeBookedEventDialog}
              switchToReassignMode={modeState.switchToReassignMode}
              allSidebarItems={legendItems}
              resetToDefaultMode={modeState.resetToDefaultMode}
              refreshSlot={refreshBookedSlots}
            />
          </Dialog>

          <Dialog
            open={modeState.showReschedulePopup}
            onClose={modeState.closeReschedulePopup}
            PaperComponent={BookingInfoDialogPaper}
          >
            <ReschedulePopup
              bookedEventInfo={modeState.selectedBookedEvent}
              newStartTime={modeState.timeToRescheduleTo}
              afterActionFunction={afterRescheduling}
              closeDialog={modeState.closeReschedulePopup}
              allSidebarItems={legendItems}
            />
          </Dialog>
        </>
      )}
    </RosterPageContainer>
  )
}
